import {Button, Modal} from 'antd'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault} from 'views/Shared'
import EventContext from '../EventContext'
import {eventSchema} from "../../../../apis/schema/event";
import {useSelectEntities} from "../../../../redux/entities/useSelectEntities";
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import usePaginationHelper, {createPagerKey} from "../../hooks/usePaginationHelper";
import CRUD from "../../../Shared/enums/CRUD";
import {useHistory} from "../../../../modules/navigation/useRouter";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {useLocation} from "react-use";
import {LocalContext} from "../../../../modules/local/LocalContext";
import {useRouteSearchParam} from "../../../../envs/_current/history";
import createContextMenu from "../../factory/createContextMenu";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import Async from "../../../../modules/asyncCache/components/Async";
import {DelayRender} from "../../../Shared/components/DelayRender";
import {useEventLocationApi} from "../functions/useEventLocationApi";
import {event_location_getById_Api, event_location_getEventLocations_Api} from "../../../../apis/event";
import {renderEventLocationItem} from "../functions/renderEventLocationItem";
import {CreateEventLocationForm, DetailViewForm, EditEventLocationForm} from "../ActionForms/EventLocationForm";
import {LeafletMap} from "../../../../components/form/fields/LeafletMap";
import {EventLocationsFilter} from "../holders/EventLocations";
import {NoneStickyMeta} from "../../EntityList";
import LoadingBlock from "../../../Loading/LoadingBlock";
import {RiSearchEyeLine} from "react-icons/ri";

const EventSettingLocations = ({
                                   filter_actions = {
                                       sortBy: 'sort-by',
                                       keyword: 'keyword',
                                       diocese_id: 'diocese',
                                   },
                               }) => {
    const {event: _event} = useContext(EventContext)
    const event = useSelectEntities(getId(_event), eventSchema)

    const t = useTranslate()
    const history = useHistory()
    const {isSm, isLg} = useContext(LayoutContext)
    const location = useLocation()
    const {lang} = useContext(LocalContext)

    const [refreshKey, setRefreshKey] = useState()

    const [action, setAction] = useState({type: undefined, value: undefined})

    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const keyword = useRouteSearchParam(filter_actions.keyword)
    const diocese_id = useRouteSearchParam(filter_actions.diocese_id)

    const refresh_deps = useMemo(
        () => [
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [refreshKey, location.state]
    )

    const onCancel = () => {
        setAction({})
    }

    const onSuccess = () => {
        onCancel()
        setRefreshKey(Date.now())
    }

    const {
        createEventLocation,
        updateEventLocation,
        deleteEventLocation
    } = useEventLocationApi({
        notifyListeners: {
            onCreated: onSuccess,
            onUpdated: onSuccess,
            onDeleted: onSuccess,
        },
    })

    const renderContextMenu = useCallback((item) => {
        return createContextMenu(
            [
                {
                    icon: <RiSearchEyeLine size={14} className="text-color-400"/>,
                    label: <span className="text-sm text-color-200">{t('view detail')}</span>,
                    key: CRUD.READ,
                },
                {
                    icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                    label: <span className="text-sm text-color-200">{t('edit')}</span>,
                    key: CRUD.UPDATE,
                    invisible: !!!item?.edit
                },
                {
                    icon: <BiTrash size={14} className="text-primary-300"/>,
                    label: <span className="text-sm text-color-200">{t('delete')}</span>,
                    key: CRUD.DELETE,
                    invisible: !!!item?.edit
                },
            ].filter(e => item && (!!!e.invisible)),
            (key) => {
                switch (key) {
                    case CRUD.READ:
                        setAction({type: key, value: item})
                        break
                    case CRUD.UPDATE:
                        setAction({type: key, value: item})
                        break
                    case CRUD.DELETE:
                        const id = item?.id
                        if (id) {
                            showDeleteConfirmDialog({
                                translate: t,
                                title: 'delete this',
                                message: 'are you sure you want to delete this',
                                onOk: () => {
                                    deleteEventLocation({}, {id,})
                                },
                            })
                        }
                        break
                    default:
                        break
                }
            }
        )
    }, [])

    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        isToggleDefault: false,
        AdvancedFilter: EventLocationsFilter,
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        pagerInfo: {
            RenderEntity: React.Fragment,
            query: bindQueryParam({id: event?.id}),
            PaginationMeta: NullAsDefault,
            apiInfo: event_location_getEventLocations_Api,
            renderItem: renderEventLocationItem({
                event: event,
                history: history,
                isLg,
                translate: t,
                renderContextMenu,
                handleAction: (type, value) => {
                    setAction({type, value});
                }
            }),
            renderWrap: (child) => (
                <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            )
        },
        withValues: (values) => ({
            ...values,
        }),
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            // onSortChange(sortBy)
            onFilterChange({
                keyword,
                diocese_id,
            })
        },
        filter_params: {
            keyword,
            diocese_id,
        },
        dependencies: [
            keyword,
            diocese_id,
        ],
    })

    const modal = useMemo(() => {
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({initialValues: {}})
        let params = {}
        let modal_params = {}
        switch (action.type) {
            case CRUD.READ:
                modal_params = {
                    width: 600,
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('view location information')}
                        </div>
                    ),
                }
                params = {
                    item: action?.value,
                    onCancel,
                }
                Content = ({...rest}) => (
                    <Async
                        {...{
                            apiInfo: event_location_getById_Api,
                            query: {':id': action?.value?.id}
                        }}>
                        {(data) => {
                            const dataItem = _.get(data, 'response.data', action?.value)
                            return (
                                <DetailViewForm
                                    {...rest}
                                    item={dataItem}
                                />
                            )
                        }}
                    </Async>
                )
                break
            case CRUD.CREATE:
                params = {
                    onSubmit: (values) => {
                        createEventLocation(values, {id: event?.id})
                    },
                }
                modal_params = {
                    width: 640,
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('create new event location')}
                        </div>
                    ),
                }
                Content = CreateEventLocationForm
                break
            case CRUD.UPDATE:
                params = {
                    onSubmit: (values) => {
                        updateEventLocation(values, {id: action.value?.id})
                    },
                }
                modal_params = {
                    width: 740,
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('edit event location info')}
                        </div>
                    ),
                }
                Content = ({...rest}) => (
                    <Async
                        {...{
                            apiInfo: event_location_getById_Api,
                            query: {':id': action?.value?.id}
                        }}>
                        {
                            (data) => {
                                const dataItem = _.get(data, 'response.data', action?.value)
                                return (
                                    <EditEventLocationForm
                                        {...rest}
                                        item={dataItem}
                                        initialValues={dataItem}
                                    />
                                )
                            }
                        }
                    </Async>
                )

                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                open={action.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <DelayRender lazy={true}>
                    <Wrapper>
                        {({isLoading, initialValues}) => {
                            if (!!isLoading) {
                                return <LoadingBlock/>
                            }
                            return (
                                <Content
                                    {...{
                                        onCancel,
                                        item: action.value,
                                    }}
                                    {...params}
                                    initialValues={initialValues}
                                />
                            )
                        }}
                    </Wrapper>
                </DelayRender>
            </Modal>
        )
    }, [action])

    const coordinates = _.get(event, 'event_locations', []).map((e, i) => {
        return {
            name: `${e.name || ""}.\n${e.address || ""}`,
            longitude: Number(e?.longitude),
            latitude: Number(e?.latitude),
        }
    });

    return (
        <div className="mt-3 flex flex-col space-y-3">
            <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('locations')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
                <Button
                    type="primary"
                    onClick={() => {
                        setAction({type: CRUD.CREATE})
                    }}
                    className="rounded-lg flex flex-col gap-3 no-border font-medium text-sm">
                    {t('create new')}
                </Button>
            </div>
            {coordinates?.length > 0 ? (
                <React.Fragment>
                    <LeafletMap
                        style={{
                            height: '30vh',
                        }}
                        zoom={12}
                        zoomControl={false}
                        coordinates={coordinates}
                        longitude={coordinates[0]?.longitude ?? 0}
                        latitude={coordinates[0]?.latitude ?? 0}
                    />
                    <div className="h-1"/>
                </React.Fragment>
            ) : null}
            {pager_widget}
            {modal}
            <div className="h-12"/>
        </div>
    )
}

export default EventSettingLocations
